import type { ChatSettings, UpdateChatSettingsRequest } from '@nucleus-commerce/api-client/admin';

const isGroup = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Folds a save's answer into the cached settings, but only the keys that save sent. Every PATCH
 * answers the whole settings, and when the save bar saves several tabs at once the answers can
 * arrive in any order: taking a whole answer could put back another tab's older values. Each key
 * belongs to exactly one tab (Hours sends `behaviour.autoAssign` alone, Behaviour the rest of
 * `behaviour`), so copying only the sent keys is right whatever the order.
 */
export function mergeSavedSettings(
  cached: ChatSettings | undefined,
  patch: UpdateChatSettingsRequest,
  saved: ChatSettings,
): ChatSettings {
  if (!cached) return saved;
  const next: Record<string, unknown> = { ...cached };
  const answer = saved as unknown as Record<string, unknown>;
  for (const [key, sent] of Object.entries(patch) as Array<[string, unknown]>) {
    if (sent === undefined) continue;
    const current = next[key];
    const fresh = answer[key];
    if (isGroup(sent) && isGroup(current) && isGroup(fresh)) {
      const group: Record<string, unknown> = { ...current };
      for (const field of Object.keys(sent)) group[field] = fresh[field];
      next[key] = group;
    } else {
      next[key] = fresh;
    }
  }
  return next as unknown as ChatSettings;
}
